import { debounce } from 'lodash';
import React, { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Cascader,
  Select,
  SelectOption,
  SelectValue,
} from 'tdesign-react';
import { TreeOptionData } from 'tdesign-react/es/common';

import styles from './index.module.less';

export interface DebounceSelectProps<SelectOption = any>
  extends Omit<any, 'options' | 'children'> {
  debounceTimeout?: number;
  hasSearchOpt?: boolean;
  defaultOptions?: SelectOption[];
  type?: 'select' | 'cascader';
  relateValue?: string[];
  openUpdateOptions?: boolean; // 点击展开面板就请求fetchOptions
  useFormData?: boolean;
  onlyForm?: boolean; // 只用于form表单数据，避免下拉框ui先于值改变
  firstUpdate?: boolean; // 首次加载请求fetchOptions
  searchLoading?: boolean;
  popupVisible?: boolean;
  overlayInnerStyle?: CSSProperties;
  selectRef?: React.RefObject<any>;
  wrapperStyle?: CSSProperties;
  button?: {
    text: string;
    url: string;
  };
  fetchOptions: (
    search?: string,
    params?: { page: number;[key: string]: any }
  ) => Promise<{
    list: SelectOption[];
    pageInfo: {
      total: number;
      current: number;
      pageSize: number;
    };
  }>;
}

export const DebounceSelect: React.FC<DebounceSelectProps> = ({
  fetchOptions,
  debounceTimeout = 800,
  relateValue = [],
  bottomAdd,
  type = 'select',
  button = { text: '', url: '' },
  hasSearchOpt = false,
  openUpdateOptions = false,
  multiple = false,
  useFormData = false,
  onlyForm = false,
  searchLoading = false,
  firstUpdate = true,
  overlayInnerStyle,
  ...props
}) => {
  const [popupVisible, setPopupVisible] = useState(false || props.popupVisible);
  const [options, setOptions] = useState<SelectOption[] | TreeOptionData[]>([]);
  const [dependOptions, setDependOptions] = useState<
  { label: string; value: string }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageInfo, setPageInfo] = useState({ total: 0, pageSize: 10 });

  const fetchRef = useRef(0);
  const onceRef = useRef(true);

  const debounceFetcher = useMemo(() => {
    const loadOptions = (value: string) => {
      if (props.dependOwner) {
        const newOptions = dependOptions.filter(item => `${item.value}`.includes(value) || item.label?.includes(value));
        setOptions(newOptions);
        return;
      }

      fetchRef.current += 1;
      const fetchId = fetchRef.current;
      setOptions([]);

      let params = {
        page,
      };
      if (relateValue.length > 0) {
        params = props.form.getFieldsValue(relateValue);
      }

      fetchOptions(value, params).then((res) => {
        if (fetchId !== fetchRef.current) {
          // for fetch callback order
          return;
        }
        const newOptions = res?.list || [];
        if (hasSearchOpt) {
          newOptions.splice(0, 0, { label: value, value });
        }
        setOptions(newOptions);
        setPageInfo(res?.pageInfo);
      });
    };
    return debounce(loadOptions, debounceTimeout);
  }, [fetchOptions, debounceTimeout, dependOptions]);

  const handleClosePopup = () => {
    setPopupVisible(false);
    setPage(1);
  };

  const handleOpenPopup = () => {
    setPopupVisible(true);
    if (openUpdateOptions) {
      setLoading(true);
      updateOptions();
      setLoading(false);
    }
  };

  const handleChange = (value: SelectValue, content: any) => {
    let opts = content?.selectedOptions?.[0]?.opt;
    if (multiple && Array.isArray(value)) {
      const result = options.filter(item => value.indexOf((item as { value: string })?.value) !== -1);
      opts = result?.map((item: any) => item.opt);
    }
    props.onChange?.(value, opts);
    handleClosePopup();
  };

  /** 仅执行一次，设置默认值 */
  useEffect(() => {
    if (!onceRef.current || !firstUpdate) {
      return;
    }
    onceRef.current = false;
    updateOptions(props?.value);
  }, [props.value]);

  useEffect(() => {
    if (props.defaultOptions) {
      setOptions(props.defaultOptions);
    }
  }, [props.defaultOptions]);

  useEffect(() => {
    if (props.popupVisible) {
      handleOpenPopup();
    }
  }, [props.popupVisible]);

  const updateOptions = async (
    search?: string,
    params?: { page: number },
    concat?: boolean,
  ) => {
    if (fetchOptions) {
      const res = (await fetchOptions?.(search, params)) || [];
      const newOptions = res?.list || [];
      const resultOptions = concat ? options.concat(newOptions) : newOptions;
      setOptions(resultOptions);
      setDependOptions(resultOptions);
      setPageInfo(res?.pageInfo);
    }
  };

  // 直接使用滚动触底事件
  const handleScrollToBottom = () => {
    if (page < pageInfo?.total / pageInfo?.pageSize) {
      const newPage = page + 1;
      setPage(newPage);
      updateOptions('', { page: newPage }, true);
    }
  };

  if (type === 'cascader') {
    return <Cascader {...props} options={options as TreeOptionData[]} />;
  }

  const value = onlyForm
    ? props.formData
    : (useFormData
      ? props.formData || props.value
      : props.value || props.formData);

  return (
    <div ref={props.selectRef} style={{ ...props.wrapperStyle }} className={styles.group}>
      <Select
        {...props}
        value={value}
        multiple={multiple}
        filterable={true}
        defaultPopupVisible={false}
        popupVisible={popupVisible}
        defaultValue={props?.hasDefault && props?.defaultValue === undefined
          ? (options as TreeOptionData)[0]?.value : undefined}
        options={options as SelectOption[]}
        loading={loading || searchLoading}
        popupProps={{
          visible: popupVisible,
          destroyOnClose: true,
          overlayClassName: styles[`selectPopup-${popupVisible}`],
          overlayInnerClassName: styles.botSelect,
          overlayInnerStyle: { ...overlayInnerStyle },
          onScrollToBottom: handleScrollToBottom,
        }}
        scroll={props.scroll || { type: 'virtual' }}
        onSearch={debounceFetcher}
        onChange={handleChange}
        onFocus={handleOpenPopup}
        onBlur={handleClosePopup}
      />
      {button?.text && button?.url ? (
        <Link to={button.url} target="_blank">
          <Button className={styles.button} theme="primary">
            {button.text}
          </Button>
        </Link>
      ) : null}
    </div>
  );
};
